import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ShieldCheck, FileText } from "lucide-react";
import { BACKEND_URL } from "@/constants/api";
import { LEGAL_VERSIONS } from "@/constants/legal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal, ModalBody, ModalFooter } from "@/components/modals/Modal";

export function CreateSchoolModal({ open, onClose }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [sdaAgreed, setSdaAgreed] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleClose = () => {
    setName("");
    setConfirmed(false);
    setSdaAgreed(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !confirmed || !sdaAgreed) return;

    setCreating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        // The backend requires the accepted School Data Agreement version.
        body: JSON.stringify({ name: name.trim(), sdaVersion: LEGAL_VERSIONS.sda }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to create school");
      }
      const school = await res.json();
      toast.success("School created!");
      handleClose();
      navigate("/schools");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create a School"
      subtitle="Invite teachers and manage Report Folder sharing centrally."
      icon={<Building2 size={16} className="text-primary" />}
      maxWidth="max-w-lg"
      preventClose={creating}
    >
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <ModalBody className="space-y-5">
        {/* School name */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            School Name <span className="text-destructive/80 font-normal">*</span>
          </Label>
          <Input
            placeholder="e.g. Hillside Public School"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="h-10"
          />
        </div>

        {/* Admin confirmation */}
        <div className="rounded-xl border border-border bg-muted/30 px-4 py-3.5">
          <div className="flex items-start gap-3">
            <ShieldCheck size={15} className="text-primary mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground leading-snug">
                You will become the admin of this school
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                As admin, you are responsible for managing member access and Report Folder oversight.
                Make sure you are signed in with the correct account.
              </p>
              <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-xs text-foreground">
                  I confirm this is the correct account for admin access
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* School Data Agreement */}
        <div className="rounded-xl border border-border bg-muted/30 px-4 py-3.5">
          <div className="flex items-start gap-3">
            <FileText size={15} className="text-primary mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground leading-snug">
                School Data Agreement
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Creating a school means EasyReports processes student data on the School&rsquo;s behalf.
                Please review and accept the data agreement to continue.
              </p>
              <label className="flex items-start gap-2 mt-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={sdaAgreed}
                  onChange={(e) => setSdaAgreed(e.target.checked)}
                  className="w-4 h-4 rounded accent-primary mt-0.5"
                />
                <span className="text-xs text-foreground">
                  I have the authority to enter into and agree to the{" "}
                  <a
                    href="/school-data-agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    School Data Agreement
                  </a>{" "}
                  on behalf of this school.
                </span>
              </label>
            </div>
          </div>
        </div>

      </ModalBody>
      <ModalFooter>
        <Button
          type="submit"
          disabled={creating || !name.trim() || !confirmed || !sdaAgreed}
          className="flex-1"
        >
          {creating ? "Creating..." : "Create School"}
        </Button>
        <Button type="button" variant="outline" onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
      </ModalFooter>
      </form>
    </Modal>
  );
}
